import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { textFrom911Api } from '../textFrom911/textFrom911Api';

/** Which call-side conversation a message belongs to. */
export type TalkJSMessageSource = 'tf911' | 'collaborator' | 'agencyShare';

/**
 * Normalized projection of a TalkJS message stored locally for fast
 * substring searching. Only messages that carry a non-empty `text`
 * survive the loader — media-only / location messages are filtered out.
 */
export interface TalkJSMessageRecord {
  /** The phone-number key from the call queue this message is tied to. */
  callerId: string;
  source: TalkJSMessageSource;
  /** The displayed message body. */
  text: string;
}

interface TalkJSMessagesState {
  /** All known text messages, keyed by conversation id. */
  allTextMessages: Record<string, TalkJSMessageRecord[]>;
}

const initialState: TalkJSMessagesState = {
  allTextMessages: {},
};

const talkjsMessagesSlice = createSlice({
  name: 'talkjsMessages',
  initialState,
  reducers: {
    clearConversation(
      state,
      action: PayloadAction<{ conversationId: string }>,
    ) {
      delete state.allTextMessages[action.payload.conversationId];
    },
  },
  extraReducers: builder => {
    // Auto-populate from getConversationMessages. callerId + source are
    // forwarded via meta.arg so we can stamp each record without a
    // follow-up dispatch from the caller.
    builder.addMatcher(
      textFrom911Api.endpoints.getConversationMessages.matchFulfilled,
      (state, action) => {
        const { conversationId, callerId, source } =
          action.meta.arg.originalArgs;
        if (!conversationId || !callerId || !source) return;

        const records: TalkJSMessageRecord[] = [];
        for (const msg of action.payload.messages ?? []) {
          if (!msg?.text) continue;
          records.push({
            callerId,
            source: source as TalkJSMessageSource,
            text: msg.text,
          });
        }

        state.allTextMessages[conversationId] = records;
      },
    );
  },
});

export const { clearConversation } = talkjsMessagesSlice.actions;

export default talkjsMessagesSlice.reducer;
